import { ApiHelper, AppHelper } from '../helpers';
import { Button, DefaultCard, DefaultContent, GruposEleitoresCardList } from '../components';
import { EleicaoResponseType, GrupoEleitoresResponseType } from '../types';
import { FaPlus, FaUsers } from 'react-icons/fa';
import { FunctionComponent, useEffect, useState } from 'react';
import { LayoutEnum, SituacaoEleicaoEnum } from '../enums';
import { useAuth, useAuthAxios, useErrorHandler, useErrorStatus, useLayout } from '../hooks';
import { useNavigate, useParams } from 'react-router-dom';

import { Spinner } from '../utils';
import { StatusCodes } from 'http-status-codes';
import { useTranslation } from 'react-i18next';

const GruposEleitoresListView: FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const axios = useAuthAxios();
  const { idEleicao } = useParams();
  const [eleicao, setEleicao] = useState<EleicaoResponseType>();
  const [loadingEleicao, setLoadingEleicao] = useState(true);
  const [gruposEleitores, setGruposEleitores] = useState<GrupoEleitoresResponseType[]>([]);
  const [loadingGruposEleitores, setLoadingGruposEleitores] = useState(true);
  const [generatingRelatorioEleitoresValidos, setGeneratingRelatorioEleitoresValidos] = useState(false);
  const { setErrorStatus } = useErrorStatus();
  const errorHandler = useErrorHandler();
  const { user } = useAuth();
  const permiteAlterarDadosEleicao = eleicao?.situacao === SituacaoEleicaoEnum.NAO_PUBLICADA.key;
  const { setLayout } = useLayout();

  useEffect(() => {
    setLayout(LayoutEnum.BASE_LAYOUT);
  }, []);

  useEffect(() => {
    if (idEleicao) {
      if (ApiHelper.isValidIntegerToApi(idEleicao)) {
        axios
          .get(ApiHelper.getUrl.apiSCE.eleicao(+idEleicao))
          .then((response) => {
            setEleicao(response.data);
            axios
              .get(ApiHelper.getUrl.apiSCE.gruposEleitores(+idEleicao!))
              .then((response) => setGruposEleitores(response.data))
              .catch((error) => errorHandler(error, t('erro.recuperar_grupos_eleitores')))
              .finally(() => setLoadingGruposEleitores(false));
          })
          .catch((error) => {
            if (error.response.status === StatusCodes.NOT_FOUND) {
              setErrorStatus(error.response.status);
            } else {
              errorHandler(error, t('erro.recuperar_eleicao'));
            }
          })
          .finally(() => setLoadingEleicao(false));
      } else {
        setTimeout(() => setErrorStatus(StatusCodes.NOT_FOUND));
      }
    }
  }, [idEleicao]);

  return loadingEleicao || loadingGruposEleitores ? (
    <Spinner />
  ) : (
    <DefaultContent
      title={t('label.alterar_grupos_eleitores')}
      subtitle={eleicao?.titulo!}
      pageAction={<Button title={t('label.voltar')} onClick={() => navigate(`/eleicoes/${idEleicao}`)} outline />}
    >
      <DefaultCard
        cardTitle={t('label.grupos_eleitores')}
        headerClassName={'responsive-spacing'}
        helpLink={
          'https://conhecimento.sti.ufpb.br/books/sistema-de-cadastro-de-eleicoes/page/gerenciar-os-grupos-de-eleitores'
        }
        mainAction={() => navigate(`/eleicoes/${idEleicao}/grupos-eleitores/cadastrar`)}
        mainActionName={t('label.cadastrar_novo_grupo')}
        mainActionIcon={FaPlus}
        mainActionDisabled={!permiteAlterarDadosEleicao}
        empty={gruposEleitores.length <= 0}
        emptyIcon={FaUsers}
        emptyMessage={t('texto.nenhum_grupo_eleitores')}
        singleCardPage
        headerAction={
          gruposEleitores.length > 0 && (
            <>
              <Button
                title={t('label.relatorio_eleitores_validos')}
                className="me-2"
                showSpinner={generatingRelatorioEleitoresValidos}
                disabled={eleicao?.situacao !== SituacaoEleicaoEnum.PUBLICADA.key || generatingRelatorioEleitoresValidos}
                onClick={() => {
                  setGeneratingRelatorioEleitoresValidos(true);
                  AppHelper.downloadFileAPI(
                    ApiHelper.getUrl.apiSCE.relatorioEleitores(eleicao?.id!),
                    errorHandler,
                    t('erro.gerar_relatorio_eleitores_validos'),
                    { headers: { Authorization: `Bearer ${user?.access_token}` } },
                    () => setGeneratingRelatorioEleitoresValidos(false),
                  );
                }}
                outline
              />
            </>
          )
        }
      >
        <GruposEleitoresCardList
          idEleicao={+idEleicao!}
          gruposEleitores={gruposEleitores}
          setGruposEleitores={setGruposEleitores}
          permiteAlterarDadosEleicao={permiteAlterarDadosEleicao}
        />
      </DefaultCard>
    </DefaultContent>
  );
};

export default GruposEleitoresListView;
